import { apiClient } from "../../lib/http/apiClient";

/** Axios 1.x abort (React StrictMode remount / route change). */
export function isRequestAborted(err: unknown): boolean {
  return (
    typeof err === "object" &&
    err !== null &&
    "code" in err &&
    (err as { code?: string }).code === "ERR_CANCELED"
  );
}
import type {
  CategoryV2,
  Deal,
  HomeDealBanner,
  TypedDealCreateWithUploadResponse,
  TypedDealMutationResponse,
} from "./types";

function inferCollectionFromDeal(item: Deal): string {
  // Prefer authoritative bridge fields (updated after AI reclassification) over stale item_type.
  if (item.typed_inventory_collection) return String(item.typed_inventory_collection);
  if (item.deal_record_collection) return String(item.deal_record_collection);
  if (item.collection) return String(item.collection);
  const byGroup: Record<string, string> = {
    stays: "deal_stays",
    hotels: "deal_stays",
    packages: "deal_packages",
    flights: "deal_flights",
    cruises: "deal_cruises",
    transport: "deal_transfers",
    transfers: "deal_transfers",
    experiences: "deal_experiences",
    services: "deal_services",
    media: "deal_media",
  };
  const byType: Record<string, string> = {
    STAY: "deal_stays",
    HOTEL: "deal_stays",
    AI_HOTEL: "deal_stays",
    PACKAGE: "deal_packages",
    FLIGHT: "deal_flights",
    CRUISE: "deal_cruises",
    TRANSFER: "deal_transfers",
    EXPERIENCE: "deal_experiences",
    NIGHTLIFE: "deal_experiences",
    SERVICE: "deal_services",
    MEDIA: "deal_media",
    FLYER_VIDEO: "deal_media",
  };
  const groupKey = String(item.categories?.primary?.group_slug || item.category_group || "").toLowerCase();
  const typeKey = String(item.item_type || "").toUpperCase();
  return byGroup[groupKey] || byType[typeKey] || "";
}

/**
 * Backend now returns typed deals with assets nested under `media.{image, image_clean,
 * pdf_url, video_url, gallery}` (already presigned). Older callers and the legacy `deals`
 * shadow row still read flat `image` / `video_url` keys on the Deal. Mirror the canonical
 * `media.*` values into those legacy fields so every consumer keeps working without each
 * component having to know about the new shape.
 */
function mirrorMediaToFlat(item: Deal): Deal {
  const media = item.media as Deal["media"] | undefined;
  if (!media) return item;
  const imageClean = item.image_clean || media.image_clean || undefined;
  const image = imageClean || item.image || media.image || undefined;
  const videoUrl = item.video_url || media.video_url || undefined;
  const pdfUrl = item.pdf_url || media.pdf_url || undefined;
  return {
    ...item,
    image: image ?? item.image,
    image_clean: imageClean ?? item.image_clean,
    video_url: videoUrl ?? item.video_url,
    pdf_url: pdfUrl ?? item.pdf_url,
  };
}

/**
 * Backend now consolidates `categories`, `category_slug`, `destination`, `extras`,
 * `media`, `pricing`, `tags`, `validity`, and the group-specific block
 * (`stay`/`flight`/`package`/`cruise`/`transfer`/`experience`) into `ai_extracted_raw`
 * on typed-deal API responses instead of duplicating them at the top level. Rehydrate
 * them back onto the item here — once, at the fetch boundary — so every existing
 * consumer (deal cards, filters, price/extras/validity displays) keeps working
 * without having to know about `ai_extracted_raw`.
 */
const AI_RAW_HYDRATE_KEYS = [
  "categories", "category_slug", "destination", "extras", "media",
  "pricing", "tags", "validity",
  "stay", "flight", "package", "cruise", "transfer", "experience",
] as const;

function hydrateFromAiRaw(item: Deal): Deal {
  const aiRaw = (item as unknown as Record<string, unknown>).ai_extracted_raw as Record<string, unknown> | null | undefined;
  if (!aiRaw) return item;
  const patch: Record<string, unknown> = {};
  for (const key of AI_RAW_HYDRATE_KEYS) {
    const existing = (item as unknown as Record<string, unknown>)[key];
    if (existing === undefined && aiRaw[key] !== undefined) {
      patch[key] = aiRaw[key];
    }
  }
  return Object.keys(patch).length ? { ...item, ...patch } : item;
}

function normalizeInventoryDeals(items: Deal[]): Deal[] {
  return items.map((item) => mirrorMediaToFlat(hydrateFromAiRaw({
    ...item,
    collection: inferCollectionFromDeal(item),
  })));
}

/** Minimal body for POST /api/deals/upload (multipart: title, description, optional file). */
export type CreateDealPayload = {
  title?: string;
  description?: string;
};

export interface DealAnalysisJobResult extends Record<string, unknown> {
  ai_status?: string;
  ai_message?: string;
  ai_token_usage?: { prompt_tokens?: number; completion_tokens?: number; total_tokens?: number };
  ai_model?: string;
  ai_elapsed_ms?: number;
}

export interface DealAnalysisJobStatus {
  job_id: string;
  deal_id: string;
  status:
    | "queued"
    | "ocr_running"
    | "ocr_completed"
    | "llm_running"
    | "llm_completed"
    | "index_running"
    | "index_completed"
    | "completed"
    | "failed";
  stage: string;
  error?: string | null;
  error_code?: string | null;
  error_detail?: { detected_group?: string | null; seller_types?: string | null } | null;
  result?: DealAnalysisJobResult | null;
  created_at?: string;
  updated_at?: string;
  /** Set by server when the staging deal was auto-deleted after a failure */
  staging_deal_deleted?: boolean;
  /** Professional user-facing message set by server on failure */
  user_message?: string | null;
  /** Why enqueue returned this job: a fresh queue, an in-flight job, or a re-used completed one */
  enqueue_outcome?: "queued" | "already_running" | "already_analyzed";
  /** True when this exact file was already analyzed and was NOT re-run */
  already_analyzed?: boolean;
  /** Human-readable explanation of the enqueue outcome */
  message?: string | null;
  /** Server-computed elapsed time since job creation (ms) — authoritative, not client-clock based */
  elapsed_ms?: number;
  /** Server-estimated total duration for this job type (ms) — based on source_kind (video/image/etc.) */
  estimated_total_ms?: number;
}

export interface InventoryCategory {
  name: string;
  slug: string;
  count: number;
  subcategories?: Array<{
    name: string;
    slug: string;
    count: number;
    group?: string;
    entity_type?: "category" | "group";
    is_promoted?: boolean;
    icon?: string | null;
    url?: string | null;
    image_url?: string | null;
    video_url?: string | null;
  }>;
  entity_type?: "group" | "category" | null;
  is_promoted?: boolean | null;
  original_group?: string | null;
  group?: string | null;
  icon?: string | null;
  url?: string | null;
  image_url?: string | null;
  video_url?: string | null;
}

export interface InventoryCategoriesResponse {
  categories: InventoryCategory[];
  promoted_categories: InventoryCategory[];
  total_categories: number;
  total_inventory: number;
  total_deal_count: number;
  total_webinar_count: number;
  total_news_count: number;
}

export type DealCategoryRouteItem = CategoryV2;

export const dealsService = {
  async listDeals(category?: string): Promise<Deal[]> {
    return this.listInventory(category);
  },

  async listInventory(
    category?: string,
    skip = 0,
    limit = 20,
    opts?: { signal?: AbortSignal; personalized?: boolean },
  ): Promise<Deal[]> {
    const { data } = await apiClient.get<Deal[]>("/api/typed-deals", {
      params: {
        category_slug: category,
        skip,
        limit,
        personalized: opts?.personalized ? true : undefined,
      },
      signal: opts?.signal,
    });
    return normalizeInventoryDeals(Array.isArray(data) ? data : []);
  },

  /** Public, unauthenticated — GET /api/home-deal-banners. Only returns is_active banners. */
  async getHomeDealBanners(
    params?: { category_slug?: string; subcategory_slug?: string },
    opts?: { signal?: AbortSignal },
  ): Promise<HomeDealBanner[]> {
    const { data } = await apiClient.get<HomeDealBanner[]>("/api/home-deal-banners", {
      params,
      signal: opts?.signal,
    });
    return Array.isArray(data) ? data : [];
  },

  async getInventoryCategories(opts?: { signal?: AbortSignal }): Promise<InventoryCategoriesResponse> {
    const { data } = await apiClient.get<InventoryCategoriesResponse>("/api/v2/categories", {
      params: { view: "summary" },
      signal: opts?.signal,
    });
    return {
      categories: data?.categories ?? [],
      promoted_categories: data?.promoted_categories ?? [],
      total_categories: data?.total_categories ?? 0,
      total_inventory: data?.total_inventory ?? 0,
      total_deal_count: data?.total_deal_count ?? 0,
      total_webinar_count: data?.total_webinar_count ?? 0,
      total_news_count: data?.total_news_count ?? 0,
    };
  },

  async listDealCategories(
    group?: string,
    promotional?: boolean,
    opts?: { signal?: AbortSignal },
  ): Promise<DealCategoryRouteItem[]> {
    const params: Record<string, string | boolean> = {};
    if (group) params.group = group;
    if (promotional !== undefined) params.promotional = promotional;
    const { data } = await apiClient.get<DealCategoryRouteItem[]>("/api/v2/categories", {
      params,
      signal: opts?.signal,
    });
    return Array.isArray(data) ? data : [];
  },

  async setCategoryFeedPosition(
    targetType: "group" | "category",
    target: string,
    position: "up" | "down",
  ): Promise<void> {
    await apiClient.put("/api/categories/layout/position", null, {
      params: {
        target_type: targetType,
        target,
        position,
      },
    });
  },

  async uploadCategoryGroupMedia(group: string, file: File): Promise<{ image_url?: string | null; hero_image_url?: string | null }> {
    const form = new FormData();
    form.append("file", file);
    const { data } = await apiClient.post<{ image_url?: string | null; hero_image_url?: string | null }>(
      `/api/categories/groups/${encodeURIComponent(group)}/hero-image`,
      form,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return data ?? {};
  },

  async uploadCategoryMedia(slug: string, file: File): Promise<{ image_url?: string | null; hero_image_url?: string | null }> {
    const form = new FormData();
    form.append("file", file);
    const { data } = await apiClient.post<{ image_url?: string | null; hero_image_url?: string | null }>(
      `/api/categories/${encodeURIComponent(slug)}/hero-image`,
      form,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return data ?? {};
  },

  async listSavedDeals(skip = 0, limit = 200, opts?: { signal?: AbortSignal }): Promise<Deal[]> {
    try {
      const { data } = await apiClient.get<{ items?: Deal[] } | Deal[]>("/api/typed-deals/saved", {
        params: { skip, limit },
        signal: opts?.signal,
      });
      const items = Array.isArray(data) ? data : (data?.items ?? []);
      return normalizeInventoryDeals(items);
    } catch {
      const { data } = await apiClient.get<Deal[]>("/api/deals/saved", {
        params: { skip, limit },
        signal: opts?.signal,
      });
      return normalizeInventoryDeals(Array.isArray(data) ? data : []);
    }
  },

  async saveDeal(itemType: string, itemId: string): Promise<void> {
    try {
      await apiClient.post(`/api/typed-deals/saved/${encodeURIComponent(itemType)}/${encodeURIComponent(itemId)}`);
    } catch {
      await apiClient.post(`/api/deals/saved/${encodeURIComponent(itemType)}/${encodeURIComponent(itemId)}`);
    }
  },

  async unsaveDeal(itemType: string, itemId: string): Promise<void> {
    try {
      await apiClient.delete(`/api/typed-deals/saved/${encodeURIComponent(itemType)}/${encodeURIComponent(itemId)}`);
    } catch {
      await apiClient.delete(`/api/deals/saved/${encodeURIComponent(itemType)}/${encodeURIComponent(itemId)}`);
    }
  },

  async listMyInventory(skip = 0, limit = 20): Promise<Deal[]> {
    try {
      const { data } = await apiClient.get<{ items?: Deal[] } | Deal[]>(
        `/api/typed-deals/my?skip=${skip}&limit=${limit}`
      );
      const items = Array.isArray(data) ? data : (data?.items ?? []);
      return normalizeInventoryDeals(items);
    } catch {
      const { data } = await apiClient.get<{ items?: Deal[] }>(
        `/api/deals/inventory/my?skip=${skip}&limit=${limit}`
      );
      return normalizeInventoryDeals(data?.items ?? []);
    }
  },

  async likeDeal(dealId: string, collection: string, campaignId?: string): Promise<{ likes: number; liked: boolean }> {
    let resolvedCollection = collection;
    if (!resolvedCollection) {
      const resolved = await this.resolveCollection(dealId);
      if (!resolved) throw new Error("Could not determine deal collection for like");
      resolvedCollection = resolved.collection;
    }
    const { data } = await apiClient.post<{ likes: number; liked: boolean }>(
      `/api/typed-deals/${encodeURIComponent(resolvedCollection)}/${encodeURIComponent(dealId)}/like`,
      campaignId ? { campaign_id: campaignId } : {},
    );
    return data ?? { likes: 0, liked: false };
  },

  async getDeal(collection: string, dealId: string): Promise<Deal> {
    const { data } = await apiClient.get<Deal>(
      `/api/typed-deals/${encodeURIComponent(collection)}/${encodeURIComponent(dealId)}`,
    );
    return data;
  },

  /** Resolve the actual typed collection a deal lives in after AI reclassification. */
  async resolveCollection(dealId: string): Promise<{ collection: string; typedId: string } | null> {
    try {
      const { data } = await apiClient.get<{ deal_id: string; collection: string }>(
        `/api/typed-deals/resolve/${encodeURIComponent(dealId)}`,
      );
      if (!data?.collection) return null;
      return { collection: data.collection, typedId: data.deal_id ?? dealId };
    } catch {
      return null;
    }
  },

  /** Single typed deal by collection + id — the plain GET counterpart to
   *  resolveCollection() above (no ownership check server-side; safe for
   *  admin deep-links, e.g. from a duplicate-deal notification). */
  async getTypedDeal(collection: string, dealId: string): Promise<any> {
    const { data } = await apiClient.get(
      `/api/typed-deals/${encodeURIComponent(collection)}/${encodeURIComponent(dealId)}`,
    );
    return data;
  },

  async updateInventoryItem(
    collection: string,
    itemId: string,
    payload: Record<string, unknown>
  ): Promise<TypedDealMutationResponse> {
    const { data } = await apiClient.put<TypedDealMutationResponse>(
      `/api/typed-deals/${encodeURIComponent(collection)}/${encodeURIComponent(itemId)}`,
      payload
    );
    const doc = data?.document;
    if (doc && data?.id && !doc.id) {
      doc.id = data.id;
    }
    // Use collection from server response — may differ if AI reclassified the deal.
    const resolvedCollection = data?.collection || collection;
    if (doc) {
      doc.collection = resolvedCollection;
    }
    return data;
  },

  async deleteInventoryItem(collection: string, itemId: string): Promise<void> {
    await apiClient.delete(`/api/typed-deals/${encodeURIComponent(collection)}/${encodeURIComponent(itemId)}`);
  },
  /**
   * Seller create: multipart POST `/api/deals/upload/legacy` with **only** `title`,
   * `description`, and optional `file`. Category and item type are resolved on the
   * server. This legacy `frontend/` app hits the `/legacy` variant of the upload
   * route, which is identical to `/api/deals/upload` except it skips the seller's
   * deal-count quota gate — the web frontend keeps using the quota-enforced route.
   */
  async submitDealListing(
    payload: CreateDealPayload = {},
    file?: File | null,
  ): Promise<TypedDealCreateWithUploadResponse> {
    const form = new FormData();

    form.append("title", payload.title ?? "");
    form.append("description", payload.description ?? "");

    if (file) {
      form.append("file", file);
    }

    const { data } = await apiClient.post<TypedDealCreateWithUploadResponse>(
      "/api/deals/upload/legacy",
      form,
    );
    return data;
  },

  /** Alias for `submitDealListing` without a file (metadata-only stub). */
  async createDeal(payload: CreateDealPayload = {}): Promise<TypedDealMutationResponse> {
    return this.submitDealListing(payload);
  },

  /** Upload an image, PDF, doc, or video asset for a deal (multipart form). */
  async uploadDealFile(dealId: string, file: File): Promise<unknown> {
    const form = new FormData();
    form.append("file", file);
    const { data } = await apiClient.post<unknown>(
      `/api/deals/${dealId}/upload`,
      form,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    return data;
  },

  /** Upload one or more images/videos to a deal's gallery only (no AI, no reindex). */
  async uploadGalleryFiles(dealId: string, files: File[]): Promise<{ key: string; url: string }[]> {
    const results: { key: string; url: string }[] = [];
    for (const file of files) {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await apiClient.post<{ key: string; url: string }>(
        `/api/deals/${dealId}/gallery`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      results.push(data);
    }
    return results;
  },

  /** @deprecated Use uploadGalleryFiles — kept for backward compat */
  async uploadDealMedia(dealId: string, file: File): Promise<Deal> {
    const formData = new FormData();
    formData.append("file", file);
    const { data } = await apiClient.post<Deal>(
      `/api/deals/${dealId}/upload`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    return data;
  },

  /** Trigger Gemini AI analysis on the deal's uploaded flyer. */
  async analyzeDeal(dealId: string): Promise<DealAnalysisJobStatus> {
    const { data } = await apiClient.post<DealAnalysisJobStatus>(`/api/deals/${dealId}/analyze`);
    return data;
  },

  async getAnalysisJob(jobId: string): Promise<DealAnalysisJobStatus> {
    const { data } = await apiClient.get<DealAnalysisJobStatus>(`/api/deals/analysis-jobs/${jobId}`);
    return data;
  },

  async getLatestAnalysisStatus(dealId: string): Promise<DealAnalysisJobStatus> {
    const { data } = await apiClient.get<DealAnalysisJobStatus>(`/api/deals/${dealId}/analysis-status`);
    return data;
  },

  async getFilterOptions(): Promise<{ tags: string[]; activities: string[]; countries: string[]; cities: string[]; categories: string[] }> {
    const { data } = await apiClient.get<{ tags: string[]; activities: string[]; countries: string[]; cities: string[]; categories: string[] }>("/api/typed-deals/filter-options");
    return data ?? { tags: [], activities: [], countries: [], cities: [], categories: [] };
  },

  async searchDeals(params: {
    q?: string;
    countries?: string;
    cities?: string;
    tags?: string;
    activities?: string;
    category_slug?: string;
    skip?: number;
    limit?: number;
  }): Promise<Deal[]> {
    const { data } = await apiClient.get<Deal[]>("/api/typed-deals", { params });
    return normalizeInventoryDeals(Array.isArray(data) ? data : []);
  },

};

